import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
  Fragment
} from "react";
import AgentLayout from "../components/AgentLayout";
import NavButton from "../components/NavButton";
import { ResponsiveDataTable } from "../components/datatable";
import axiosInstance from "../../../utils/axiosInstance";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { refreshSessionData } from "../../../utils/refreshSessionData";
import { onChartBalanceRefresh } from "../../../utils/chartBalanceRefresh";

const TOTAL = 200;
const PAGE_SIZE = 100;

const AccountList = () => {
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [serverSearching, setServerSearching] = useState(false);
  const [paginatedMode, setPaginatedMode] = useState(false);
  const [displayUsername, setDisplayUsername] = useState("");
  const [userCount, setuserCount] = useState(0);

  const [isOpen, setOpenState] = useState(false);
  const open = useCallback(() => setOpenState(true));
  const close = useCallback(() => setOpenState(false));

  const [pageLoad, setPageLoad] = useState(false);

  const [CreditReceived, setCreditReceived] = useState(0);
  const [CreditRemaining, setCreditRemaining] = useState(0);
  const [userCash, setUserCash] = useState(0);
  const [plDownline, setPLDownline] = useState(0);
  const [BalanceUpLine, setBalanceUpLine] = useState(0);

  const [showTotal, setShowTotal] = useState(false);

  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userStore);

  const isOwnerCompany = [1, 2].includes(Number(userInfo.data?.user_role));
  const canManageDistributors = isOwnerCompany;

  const columns = [
    { key: "uname", label: "Username", mobileVisible: true },
    {
      key: "user_role",
      options: {
        1: "Owner Company",
        2: "Super Company",
        3: "Company",
        4: "SuperAdmin",
        5: "Admin",
        6: "Super Master",
        7: "Master",
        8: "Bettor"
      },
      label: "Type",
      mobileVisible: true
    },

    //{ key: "credit_amount", label: "Credit", mobileVisible: true },
    { key: "credit_reference", label: "Credit", mobileVisible: true },
    { key: "bz_balance", label: "Balance", mobileVisible: false },
    { key: "clientpl", label: "Client (P/L)", mobileVisible: false },
    { key: "my_sharing", label: "Share", mobileVisible: false },
    { key: "exposure", label: "Exposure", mobileVisible: false },
    {
      key: "availableBalance",
      label: "Available Balance",
      mobileVisible: false
    }
  ];

  const handleView = (row) => console.log("View", row);
  const handleEdit = (row) => console.log("Edit", row);
  const handleDelete = (row) => console.log("Delete", row);

  const [CDPopUpOPen, setCDPopUpOPen] = useState(false);
  const [balancesLoaded, setBalancesLoaded] = useState(false);

  const [totalBalance, setTotalBalance] = useState(0);
  const [totalClientPL, setTotalClientPL] = useState(0);
  const [totalCreditGiven, setTotalCreditGiven] = useState(0);

  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isChartPage = location.pathname.startsWith("/agent/accounts/chart");

  const [affiliateLinkCopied, setAffiliateLinkCopied] = useState(false);
  const [affiliateCode, setAffiliateCode] = useState("");
  const [affiliateRegenerating, setAffiliateRegenerating] = useState(false);
  const affiliateLink = affiliateCode
    ? `${window.location.origin}/register?ref=${affiliateCode}`
    : "";

  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [selectedSearchUser, setSelectedSearchUser] = useState(null);
  const [breadcrumbPath, setBreadcrumbPath] = useState([]);
  const userSearchWrapRef = useRef(null);
  const searchRef = useRef("");
  const skipRef = useRef(0);
  const paginatedRef = useRef(false);
  const fetchSeqRef = useRef(0);
  const inFlightRef = useRef(false);

  const loginUserId = userId != undefined ? userId : userInfo.data?._id;

  const formatTruncate = (value) => {
    let num = Math.round(value);
    const truncated = Math.floor(num * 1000) / 1000;

    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    }).format(truncated);
  };

  useEffect(() => {
    if (!isChartPage || !userInfo.data?.allow_auto_deposit) return undefined;
    let cancelled = false;
    axiosInstance
      .get("/company/affiliate-code")
      .then((res) => {
        if (!cancelled && res.data?.status) {
          const codes = res.data.data?.affiliate_codes || [];
          setAffiliateCode(codes.length ? codes[codes.length - 1] : "");
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isChartPage, userInfo.data?.allow_auto_deposit]);

  const regenerateAffiliateCode = async () => {
    if (affiliateRegenerating) return;
    setAffiliateRegenerating(true);
    try {
      const res = await axiosInstance.post("/company/affiliate-code/regenerate");
      if (res.data?.status) {
        const codes = res.data.data?.affiliate_codes || [];
        setAffiliateCode(codes.length ? codes[codes.length - 1] : "");
        setAffiliateLinkCopied(false);
      }
    } catch {
      // keep the current code if regenerate fails
    } finally {
      setAffiliateRegenerating(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const q = userSearchQuery.trim();
    if (q.length < 2) {
      setUserSearchResults([]);
      setUserSearchOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        const res = await axiosInstance.get("/company/search-downline-users", {
          params: { q }
        });
        if (cancelled) return;
        const list = Array.isArray(res.data?.users) ? res.data.users : [];
        const selectionLocked =
          selectedSearchUser && q === (selectedSearchUser.uname || "").trim();
        if (selectionLocked) {
          setUserSearchResults([]);
          setUserSearchOpen(false);
        } else {
          setUserSearchResults(list);
          setUserSearchOpen(list.length > 0);
        }
      } catch {
        if (!cancelled) {
          setUserSearchResults([]);
          setUserSearchOpen(false);
        }
      } finally {
        if (!cancelled) setUserSearchLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [userSearchQuery, selectedSearchUser]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (
        userSearchWrapRef.current &&
        !userSearchWrapRef.current.contains(e.target)
      ) {
        setUserSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const selectUserFromSuggest = useCallback((u) => {
    if (!u?._id) return;
    setSelectedSearchUser(u);
    setUserSearchQuery(u.uname || "");
    setUserSearchResults([]);
    setUserSearchOpen(false);
  }, []);

  const fetchUsersRef = useRef(null);

  const loadMore = useCallback(() => {
    if (!paginatedRef.current || inFlightRef.current || !hasMore) return;
    fetchUsersRef.current?.(false, 0, { append: true });
  }, [hasMore]);

  const showSummaryBalances = (user) => {
    if (!user) return;
    /*console.log("Summary Balance Updated:", {
      "Credit Received": user.credit_reference,
      "Credit Remaining": user.credit_amount,
      "Cash Balance": user.bz_balance,
      "P/L Downline": user.transaction_pl,
      "Balance UpLine": user.upline_balance
    });*/
    setBalancesLoaded(true);
    setCreditReceived(user.credit_reference);
    setCreditRemaining(user.credit_amount);
    setUserCash(user.bz_balance);
    setPLDownline(
      user.transaction_pl > 0
        ? -user.transaction_pl
        : Math.abs(user.transaction_pl)
    );
    setBalanceUpLine(user.upline_balance);
  };

  const showSummaryBalancesWithOutLoadBalance = (user) => {
    let loginUserId = userInfo?.data?._id;
    if (
      loginUserId != null &&
      loginUserId != undefined &&
      loginUserId == user._id
    ) {
      setUserCash(user.bz_balance);
      setPLDownline(
        user.transaction_pl > 0
          ? -user.transaction_pl
          : Math.abs(user.transaction_pl)
      );
    }
  };

  const fetchUsers = useCallback(
    async (withBalances = false, delayMs = 0, opts = {}) => {
      if (!loginUserId) return;

      const append = opts.append === true;
      const q = opts.q !== undefined ? String(opts.q) : searchRef.current;
      const seq = ++fetchSeqRef.current;

      // Add delay if specified (for race condition after balance updates)
      if (delayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        if (seq !== fetchSeqRef.current) return;
      }

      const isServerSearch =
        !append && paginatedRef.current && opts.q !== undefined;
      const keepTableVisible =
        append || isServerSearch || (paginatedRef.current && withBalances);
      if (!keepTableVisible) {
        setPageLoad(false);
      }
      if (append) setLoadingMore(true);
      if (isServerSearch) {
        setServerSearching(true);
        setRows([]);
        setHasMore(false);
      }
      inFlightRef.current = true;

      try {
        const params = {};
        if (withBalances) params._t = Date.now();

        const refreshLimit = Math.min(
          Math.max(skipRef.current || PAGE_SIZE, PAGE_SIZE),
          1000
        );
        params.limit = append
          ? PAGE_SIZE
          : withBalances && paginatedRef.current
          ? refreshLimit
          : PAGE_SIZE;
        params.skip = append ? skipRef.current : 0;
        if (
          q &&
          q.trim() &&
          (append || paginatedRef.current || opts.q !== undefined)
        ) {
          params.q = q.trim();
        }

        const response = await axiosInstance.get(
          `user/${loginUserId}/downline`,
          {
            params,
            headers: withBalances
              ? { "Cache-Control": "no-cache", Pragma: "no-cache" }
              : undefined
          }
        );
        if (seq !== fetchSeqRef.current) return;
        //console.log("downline response totals", response?.data?.totals);

        const payload = response?.data || {};
        const info = payload.userInfo;
        const downline = Array.isArray(payload.downnLine)
          ? payload.downnLine
          : [];
        const isPaginated = Number(payload.paginatedResults) === 1;

        if (!info) return;

        paginatedRef.current = isPaginated;
        setPaginatedMode(isPaginated);
        setDisplayUsername(info.uname || "");
        setTotalBalance(response?.data?.totals?.totalBalance);
        setTotalClientPL(response?.data?.totals?.totalClientPL);
        setTotalCreditGiven(response?.data?.totals?.totalCreditGiven);
        //setShowTotal(false);

        searchRef.current = q.trim();

        if (isPaginated && append) {
          if (downline.length === 0) {
            setHasMore(false);
          } else {
            setRows((prev) => {
              const seen = new Set(prev.map((row) => String(row._id)));
              const next = downline.filter((row) => !seen.has(String(row._id)));
              return next.length ? [...prev, ...next] : prev;
            });
            skipRef.current += downline.length;
            setHasMore(payload.hasMore === true);
          }
          setuserCount(Number(payload.total) || 0);
        } else {
          setRows(downline);
          skipRef.current = isPaginated ? downline.length : 0;
          setHasMore(isPaginated && payload.hasMore === true);
          setuserCount(
            isPaginated ? Number(payload.total) || 0 : downline.length
          );
        }

        if (withBalances) {
          showSummaryBalances(info);
        } else {
          showSummaryBalancesWithOutLoadBalance(info);
        }
      } catch (error) {
        if (seq !== fetchSeqRef.current) return;
        const status = error?.response?.status;
        if ((status === 403 || status === 404) && userId) {
          navigate("/agent/accounts/chart", { replace: true });
          return;
        }
        console.error("Error fetching downline:", error);
        if (append) {
          setHasMore(false);
        }
      } finally {
        if (seq === fetchSeqRef.current) {
          inFlightRef.current = false;
          setLoadingMore(false);
          setServerSearching(false);
          setPageLoad(true);
        }
      }
    },
    [loginUserId, userId, navigate]
  );

  const loadBalances = useCallback(() => {
    fetchUsers(true);
    setShowTotal(true);
  }, [fetchUsers]);

  const userLinkClick = useCallback(() => {
    setShowTotal(false);
  });

  const handleServerSearch = useCallback(
    (q) => {
      searchRef.current = q;
      skipRef.current = 0;
      fetchUsers(false, 0, { q, append: false });
    },
    [fetchUsers]
  );

  // Use ref to avoid recreating the listener on every render
  fetchUsersRef.current = fetchUsers;

  useEffect(() => {
    setBreadcrumbPath([]);
    setSelectedSearchUser(null);
    setUserSearchQuery("");
    searchRef.current = "";
    skipRef.current = 0;
    paginatedRef.current = false;
    setPaginatedMode(false);
    setHasMore(false);
  }, [loginUserId]);

  useEffect(() => {
    setCreditReceived(0);
    setCreditRemaining(0);
    setUserCash(0);
    setPLDownline(0);
    setBalanceUpLine(0);
    setBalancesLoaded(false);
    fetchUsers(false);
  }, [loginUserId, userId, fetchUsers]);

  useEffect(() => {
    return onChartBalanceRefresh(() => {
      // Add 1 second delay to allow backend to finish updating
      fetchUsersRef.current(true, 1000);
      // Also delay header refresh
      setTimeout(() => {
        refreshSessionData(dispatch).catch(console.error);
      }, 1000);
    });
  }, [dispatch]);

  const handleCreditDebit = (row) => {
    window.open(
      `/agent/popup-page/cd/${row._id}`,
      "hawalaCashCredit",
      "width=660,height=750,left=0,top=0"
    );
  };

  const openLedgerPopUp = async (id, type) => {
    let popup = null;

    if (type == "CashDeposit" || type == "CashWithdrawal") {
      popup = window.open(
        "/agent/popup-page/le/" + id,
        "",
        "width=900,height=800"
      );
    } else {
      popup = window.open(
        "/agent/popup-page/crle/" + id,
        "",
        "width=900,height=800"
      );
    }

    if (popup) {
      popup.document.title = "BPEXCH";
    }
  };

  const PDownlineDesktop = async (id) => {
    const popup = window.open(
      "/pl-downline-des/" + id,
      "",
      "width=850,height=800"
    );
    if (popup) {
      popup.document.title = "BPEXCH";
    }
  };

  const PDownline = async (id) => {
    const popup = window.open(
      "/pl-downline-view/" + id,
      "",
      "width=850,height=800"
    );
    if (popup) {
      popup.document.title = "BPEXCH";
    }
  };

  const handleLedger = async (row) => {
    let userId = row._id;
    openLedgerPopUp(userId);
  };

  const settlementPopup = async (row) => {
    let userId = row._id;
    const popup = window.open(
      "/agent/settlement/" + userId,
      "",
      "width=850,height=800"
    );
    if (popup) {
      popup.document.title = "BPEXCH";
    }
  };

  const executeUserSearch = useCallback(async () => {
    const target = selectedSearchUser;
    if (!target?._id || !loginUserId) return;
    setUserSearchLoading(true);
    try {
      const res = await axiosInstance.get(
        `/company/user-breadcrumb/${target._id}`,
        { params: { from: loginUserId } }
      );
      const path = Array.isArray(res.data?.path) ? res.data.path : [];
      setBreadcrumbPath(path);
      setUserSearchOpen(false);
    } catch {
      setBreadcrumbPath([]);
    } finally {
      setUserSearchLoading(false);
    }
  }, [selectedSearchUser, loginUserId]);

  const searchSelectionLocked =
    !!selectedSearchUser &&
    userSearchQuery.trim() === (selectedSearchUser.uname || "").trim();

  const [bgColor, setBgColor] = useState(false);
  useEffect(() => {
    if (isMobile) {
      setBgColor("#ffffff");
    } else {
      setBgColor("#ffffff");
    }
  });

  return (
    <AgentLayout>
      <div className="container-fluid">
        <div className="loader" id="data_loaded" style={{ display: "none" }}>
          <img src="/assets/images/loadinggif.gif" />
        </div>

        <div className="bets-section-wrapper">
          <div>
            <NavButton></NavButton>
          </div>

          {isChartPage &&
            !!userInfo.data?.allow_auto_deposit &&
            affiliateLink && (
              <div
                className="card col-12"
                style={{ marginBottom: "10px", paddingLeft: "0px" }}
              >
                <div className="card-header">
                  <i className="fa fa-link"></i>
                  <strong className="site_family">
                    {" "}
                    Affiliate Registration Link
                  </strong>
                </div>
                <div className="card-body" style={{ padding: "12px 16px" }}>
                  <p
                    className="site_family"
                    style={{
                      fontSize: "13px",
                      marginBottom: "8px",
                      color: "#555"
                    }}
                  >
                    Share this link with users to let them register under your
                    account.
                  </p>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "8px"
                    }}
                  >
                    <input
                      type="text"
                      readOnly
                      value={affiliateLink}
                      className="form-control site_family"
                      style={{
                        flex: "1 1 auto",
                        minWidth: "200px",
                        fontSize: "13px",
                        backgroundColor: "#f8f9fa",
                        cursor: "text"
                      }}
                      onFocus={(e) => e.target.select()}
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-sm site_family"
                      style={{ whiteSpace: "nowrap" }}
                      onClick={() => {
                        const showCopied = () => {
                          setAffiliateLinkCopied(true);
                          setTimeout(() => setAffiliateLinkCopied(false), 2000);
                        };
                        const copyFromBox = () => {
                          const box = document.createElement("textarea");
                          box.value = affiliateLink;
                          document.body.appendChild(box);
                          box.select();
                          document.execCommand("copy");
                          document.body.removeChild(box);
                          showCopied();
                        };
                        if (navigator.clipboard && navigator.clipboard.writeText) {
                          navigator.clipboard
                            .writeText(affiliateLink)
                            .then(showCopied)
                            .catch(copyFromBox);
                        } else {
                          copyFromBox();
                        }
                      }}
                    >
                      {affiliateLinkCopied ? (
                        <>
                          <i className="fa fa-check"></i> Copied!
                        </>
                      ) : (
                        <>
                          <i className="fa fa-copy"></i> Copy
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm site_family"
                      style={{ whiteSpace: "nowrap" }}
                      disabled={affiliateRegenerating}
                      onClick={regenerateAffiliateCode}
                    >
                      {affiliateRegenerating ? "Regenerating..." : "Regenerate"}
                    </button>
                  </div>
                </div>
              </div>
            )}

          <div
            className="card col-12"
            id="Usersearchcustom"
            style={{ minHeight: "140px", overflow: "visible" }}
          >
            <div className="card-header">
              <i className="fa fa-filter"></i>
              <strong className="site_family"> Search-Users</strong>
            </div>
            <div
              className="card-body"
              style={{ paddingBottom: "0px", margin: "0px" }}
            >
              <div>
                <form autoComplete="off" className="form-horizontal">
                  <div className="form-group row">
                    <div
                      className="col-12 autocomplete position-relative"
                      ref={userSearchWrapRef}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          width: "100%",
                          columnGap: 8,
                          rowGap: 4
                        }}
                      >
                        <div
                          className="input-group"
                          style={{
                            flex: "0 0 auto",
                            maxWidth: isMobile ? "100%" : "20%",
                            minWidth: 0,
                            marginTop: "16px"
                          }}
                        >
                          <div
                            className="inputWithIcon position-relative"
                            style={{ flex: "1 1 0", minWidth: 0 }}
                          >
                            <input
                              type="text"
                              placeholder="Username"
                              id="myInput"
                              className="form-control ui-autocomplete-input site_family"
                              autoComplete="off"
                              value={userSearchQuery}
                              onChange={(e) => {
                                const v = e.target.value;
                                setUserSearchQuery(v);
                                if (
                                  selectedSearchUser &&
                                  v.trim() !==
                                    (selectedSearchUser.uname || "").trim()
                                ) {
                                  setSelectedSearchUser(null);
                                }
                              }}
                              onFocus={() => {
                                if (
                                  !searchSelectionLocked &&
                                  userSearchResults.length > 0
                                ) {
                                  setUserSearchOpen(true);
                                }
                              }}
                              aria-controls="user-search-suggest-list"
                              aria-expanded={userSearchOpen}
                            />
                            {userSearchOpen &&
                              !searchSelectionLocked &&
                              userSearchQuery.trim().length >= 2 &&
                              userSearchResults.length > 0 && (
                                <ul
                                  id="user-search-suggest-list"
                                  className="list-group position-absolute shadow-sm border bg-white mt-1"
                                  role="listbox"
                                  style={{
                                    zIndex: 1050,
                                    left: 0,
                                    top: "100%",
                                    width: "100%",
                                    maxHeight:
                                      "calc(100vh - 12rem - env(safe-area-inset-bottom, 0px))",
                                    minHeight: 0,
                                    overflowY: "auto",
                                    WebkitOverflowScrolling: "touch",
                                    boxSizing: "border-box"
                                  }}
                                >
                                  {userSearchResults.map((u) => (
                                    <li
                                      key={u._id}
                                      role="option"
                                      className="list-group-item list-group-item-action py-1 px-3 site_family"
                                      style={{ cursor: "pointer" }}
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => selectUserFromSuggest(u)}
                                    >
                                      {u.uname}
                                    </li>
                                  ))}
                                </ul>
                              )}
                          </div>

                          <span className="input-group-prepend">
                            <button
                              id="myBtn"
                              type="button"
                              className="btn btn-primary site_family"
                              onClick={() => executeUserSearch()}
                            >
                              <i className="fa fa-search"></i> Search
                            </button>
                          </span>
                        </div>
                        {breadcrumbPath.length > 0 && (
                          <nav
                            aria-label="breadcrumb"
                            id="user_search"
                            className="d-flex flex-wrap align-items-center"
                            style={{
                              flex: "1 1 12rem",
                              minWidth: 0,
                              padding: "6px 0"
                            }}
                          >
                            <div style={{ paddingRight: 8, minWidth: 0 }}>
                              <Link
                                to={`/agent/accounts/chart/${loginUserId}`}
                                aria-label="Chart root"
                              />
                              {breadcrumbPath.map((seg, i) => {
                                const isLast = i === breadcrumbPath.length - 1;
                                return (
                                  <Fragment key={seg._id}>
                                    {i === 0 && !isLast ? null : " / "}
                                    {isLast ? (
                                      <span className="site_family">
                                        <Link
                                          className="site_family"
                                          style={{ color: "#00b181" }}
                                          to={
                                            seg.user_role == 8
                                              ? `/agent/edit/${seg._id}`
                                              : `/agent/accounts/chart/${seg._id}`
                                          }
                                        >
                                          {seg.uname}
                                        </Link>
                                      </span>
                                    ) : (
                                      <Link
                                        className="site_family"
                                        style={{ color: "#00b181" }}
                                        to={
                                          seg.user_role == 8
                                            ? `/agent/edit/${seg._id}`
                                            : `/agent/accounts/chart/${seg._id}`
                                        }
                                      >
                                        {seg.uname}
                                      </Link>
                                    )}
                                  </Fragment>
                                );
                              })}
                            </div>
                          </nav>
                        )}
                      </div>
                    </div>
                  </div>
                </form>
                <img
                  src="./assets/bp/preloader.gif"
                  alt=""
                  style={{
                    height: "20px",
                    display: userSearchLoading ? "inline" : "none"
                  }}
                />
              </div>
            </div>
          </div>

          <div className="row" id="accountsChart">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5>
                    <strong className="site_family">{displayUsername}</strong> -{" "}
                    {""}
                    <span className="site_family">Clients List | Default</span>
                  </h5>
                </div>
                <div className="card-body" style={{ paddingBottom: "0px" }}>
                  <div
                    className="row col-12"
                    style={{
                      overflowX: "auto",
                      paddingLeft: "25px",
                      paddingTop: "18px"
                    }}
                  >
                    <table className="table table-bordered table-sm">
                      <tbody>
                        <tr style={{ background: bgColor }}>
                          <td
                            id="THCreditReceived"
                            style={{
                              border: "1px solid #c8ced3",
                              fontWeight: "bold",
                              padding: ".3rem",
                              display: balancesLoaded ? "" : "none"
                            }}
                            className="site_family"
                          >
                            Credit Received
                          </td>

                          <td
                            style={{
                              border: "1px solid #c8ced3",
                              fontWeight: "bold",
                              textAlign: "left",
                              padding: ".3rem;"
                            }}
                            className="site_family"
                          >
                            Credit Remaining
                          </td>
                          <td
                            style={{
                              border: "1px solid #c8ced3",
                              fontWeight: "bold",
                              textAlign: "left",
                              padding: ".3rem;"
                            }}
                            className="site_family"
                          >
                            Cash
                          </td>

                          <td
                            style={{
                              border: "1px solid #c8ced3",
                              fontWeight: "bold",
                              textAlign: "left",
                              padding: ".3rem;"
                            }}
                            className="site_family"
                          >
                            P/L Downline
                          </td>

                          <td
                            style={{
                              border: "1px solid #c8ced3",
                              fontWeight: "bold",
                              textAlign: "left",
                              padding: ".3rem",
                              display: balancesLoaded ? "" : "none"
                            }}
                            className="site_family"
                          >
                            Balance UpLine
                          </td>

                          <td
                            style={{
                              border: "1px solid #c8ced3",
                              fontWeight: "bold",
                              textAlign: "left",
                              padding: ".3rem;"
                            }}
                            className="site_family"
                          >
                            Users
                          </td>
                        </tr>

                        <tr style={{ background: "white" }}>
                          <td
                            style={{
                              border: "1px solid #c8ced3",
                              padding: ".3rem",
                              fontWeight: "600",
                              display: balancesLoaded ? "" : "none"
                            }}
                          >
                            <a
                              style={{
                                cursor: "pointer"
                              }}
                              onClick={() =>
                                openLedgerPopUp(loginUserId, "CreditDeposit")
                              }
                            >
                              <span
                                className="site_family"
                                style={{ color: "#0b6121", fontSize: "1rem" }}
                              >
                                {balancesLoaded
                                  ? formatTruncate(CreditReceived)
                                  : "0"}
                              </span>
                            </a>
                          </td>

                          <td
                            style={{
                              border: "1px solid #c8ced3",
                              textAlign: "left",
                              padding: ".3rem",
                              fontWeight: "600"
                            }}
                          >
                            <a
                              style={{
                                cursor: "pointer"
                              }}
                              className="site_family"
                              onClick={() =>
                                openLedgerPopUp(loginUserId, "CreditDeposit")
                              }
                            >
                              <span
                                style={{ color: "#0b6121", fontSize: "1rem" }}
                              >
                                {balancesLoaded
                                  ? formatTruncate(CreditRemaining)
                                  : "0"}
                              </span>
                            </a>
                          </td>

                          <td
                            style={{
                              border: "1px solid #c8ced3",
                              textAlign: "left",
                              fontSize: "1rem",
                              padding: ".3rem",
                              fontWeight: "600"
                            }}
                          >
                            <a
                              style={{
                                cursor: "pointer"
                              }}
                              onClick={() =>
                                openLedgerPopUp(loginUserId, "CashDeposit")
                              }
                            >
                              <span
                                className="site_family"
                                style={{
                                  color: userCash >= 0 ? "#0b6121" : "red",
                                  fontSize: "1rem"
                                }}
                              >
                                {formatTruncate(userCash)}
                              </span>
                            </a>
                          </td>

                          <td
                            style={{
                              border: "1px solid #c8ced3",
                              textAlign: "left",
                              fontSize: "1rem",
                              padding: ".3rem",
                              fontWeight: "600"
                            }}
                          >
                            <a
                              className="site_family"
                              style={{
                                cursor: "pointer"
                              }}
                              onClick={() =>
                                isMobile
                                  ? PDownline(loginUserId)
                                  : PDownlineDesktop(loginUserId)
                              }
                            >
                              <span
                                style={{
                                  color: plDownline >= 0 ? "#0b6121" : "red",
                                  fontSize: "1rem"
                                }}
                              >
                                {formatTruncate(plDownline)}
                              </span>
                            </a>
                          </td>

                          <td
                            style={{
                              border: "1px solid #c8ced3",
                              textAlign: "left",
                              fontSize: "1rem",
                              padding: ".3rem",
                              fontWeight: "600",
                              display: balancesLoaded ? "" : "none"
                            }}
                          >
                            <span
                              className="site_family"
                              style={{
                                color: BalanceUpLine >= 0 ? "#0b6121" : "red",
                                fontSize: "1rem"
                              }}
                            >
                              {formatTruncate(BalanceUpLine)}
                            </span>
                          </td>

                          <td
                            className="site_family"
                            style={{
                              border: "1px solid #c8ced3",
                              textAlign: "left",
                              fontSize: "1rem",
                              padding: ".3rem",
                              fontWeight: "600"
                            }}
                          >
                            {userCount}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <a
                    href="/agent/accounts/add"
                    type="button"
                    className="btn btn-sm btn-primary site_family"
                  >
                    <i className="fa fa-user-o"></i>
                    <strong>New User</strong>
                  </a>
                  <a
                    type="button"
                    className="btn btn-sm btn-primary site_family"
                    style={{ marginLeft: "10px", color: "#fff" }}
                    onClick={() =>
                      isMobile
                        ? PDownline(userInfo.data?._id)
                        : PDownlineDesktop(userInfo.data?._id)
                    }
                  >
                    <i className="fa fa-book"></i>
                    <strong>Account Ledger</strong>
                  </a>
                  {canManageDistributors || isOwnerCompany ? (
                    <a
                      href="/agent/accounts/distributors"
                      type="button"
                      className="btn btn-sm btn-primary site_family"
                      style={{ marginLeft: "10px", color: "#fff" }}
                    >
                      <i className="fa fa-users"></i>
                      <strong>Distributor</strong>
                    </a>
                  ) : null}

                  <p className="float-right">
                    <span className="btn btn-sm btn-warning mt-2">
                      <strong className="site_family">C</strong>
                    </span>{" "}
                    <span className="site_family">Cash / Credit</span>
                    <span className="btn btn-sm btn-primary mt-2">
                      <i className="fa fa-pencil" aria-hidden="true"></i>
                    </span>{" "}
                    <span className="site_family">Edit</span>
                    <span className="btn btn-sm btn-info mt-2">
                      <strong className="site_family">L</strong>
                    </span>{" "}
                    <span className="site_family">Ledger</span>
                    <span className="btn btn-sm btn-success mt-2">
                      <strong className="site_family">A</strong>
                    </span>{" "}
                    <span className="site_family">Active</span>
                    <span className="btn btn-sm btn-outline-danger mt-2">
                      <strong className="site_family">D</strong>
                    </span>{" "}
                    <span className="site_family">InActive</span>
                    {userInfo.data.can_settled == 1 ? (
                      <span>
                        <span className="btn btn-sm btn-danger">
                          <strong className="site_family">S</strong>
                        </span>
                        <span className="site_family">Settle Account</span>
                      </span>
                    ) : (
                      ""
                    )}
                  </p>
                </div>

                {/**Inserting db table here */}

                <div
                  style={{
                    width: "100%",
                    textAlign: "center",
                    display: pageLoad ? "none" : "block"
                  }}
                >
                  <img
                    src="../../../assets/images/loadinggif.gif"
                    alt="Loader"
                  />
                </div>

                {pageLoad ? (
                  <ResponsiveDataTable
                    data={rows}
                    columns={columns}
                    autoScroll={true}
                    onLoadMore={loadMore}
                    loadBalanceClick={loadBalances}
                    hasMore={paginatedMode && hasMore}
                    loadingMore={loadingMore}
                    pageSize={PAGE_SIZE}
                    serverSearch={paginatedMode}
                    onServerSearch={handleServerSearch}
                    serverSearchPending={serverSearching}
                    searchResetKey={loginUserId}
                    title=""
                    subtitle=""
                    optionsHeader="Options"
                    optionsMobileVisible={true}
                    initHide={true}
                    displayAllRows={balancesLoaded}
                    loadingClickableColumn="uname"
                    searchKey="uname"
                    showTotal={showTotal}
                    totalBalance={totalBalance}
                    totalClientPL={totalClientPL}
                    totalCreditGiven={totalCreditGiven}
                    userLinkClick={userLinkClick}
                    renderOptions={(row) => (
                      <div className="btn-group btn-group-sm">
                        <a
                          className="btn btn-warning"
                          onClick={() => handleCreditDebit(row)}
                          style={{
                            display: "inline-block",
                            fontWeight: "400",
                            textAlign: "center",
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                            userSelect: "none",
                            border: "1px solid transparent",
                            padding: ".375rem .75rem",
                            fontSize: ".875rem",
                            lineHeight: "1.5",
                            borderRadius: ".25rem",
                            transition:
                              "color .15s ease-in-out, background-color .15s ease-in-out, border-color .15s ease-in-out, box-shadow .15s ease-in-out"
                          }}
                        >
                          <strong className="site_family">C</strong>
                        </a>

                        <a
                          href={`/agent/edit/${row._id}`}
                          target="_blank"
                          className="btn btn-primary"
                          style={{
                            display: "inline-block",
                            fontWeight: "400",
                            textAlign: "center",
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                            userSelect: "none",
                            border: "1px solid transparent",
                            padding: ".375rem .75rem",
                            fontSize: ".875rem",
                            lineHeight: "1.5",
                            borderRadius: ".25rem",
                            color: "white",
                            marginLeft: "5px",
                            transition:
                              "color .15s ease-in-out, background-color .15s ease-in-out, border-color .15s ease-in-out, box-shadow .15s ease-in-out"
                          }}
                        >
                          <i className="fa fa-pencil"></i>
                        </a>

                        <a
                          className="btn btn-info"
                          onClick={() => handleLedger(row)}
                          style={{
                            display: "inline-block",
                            fontWeight: "400",
                            textAlign: "center",
                            whiteSpace: "nowrap",
                            verticalAlign: "middle",
                            userSelect: "none",
                            border: "1px solid transparent",
                            padding: ".375rem .75rem",
                            fontSize: ".875rem",
                            lineHeight: "1.5",
                            borderRadius: ".25rem",
                            marginLeft: "5px"
                          }}
                        >
                          <strong className="site_family">L</strong>
                        </a>

                        {row.stat == 1 ? (
                          <a
                            className="btn btn-success"
                            href={`/agent/edit/${row._id}`}
                            target="_blank"
                            style={{
                              display: "inline-block",
                              fontWeight: "400",
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                              userSelect: "none",
                              border: "1px solid transparent",
                              padding: ".375rem .75rem",
                              fontSize: ".875rem",
                              lineHeight: "1.5",
                              borderRadius: ".25rem",
                              marginLeft: "5px"
                            }}
                          >
                            <strong className="site_family">A</strong>
                          </a>
                        ) : (
                          <a
                            className="btn btn-outline-danger"
                            href={`/agent/edit/${row._id}`}
                            target="_blank"
                            style={{
                              display: "inline-block",
                              fontWeight: "400",
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                              userSelect: "none",
                              border: "1px solid #f86c6b",
                              padding: ".375rem .75rem",
                              fontSize: ".875rem",
                              lineHeight: "1.5",
                              borderRadius: ".25rem",
                              marginLeft: "5px"
                            }}
                          >
                            <strong className="site_family">D</strong>
                          </a>
                        )}

                        {row.user_role !== 8 && row.can_settled == 1 ? (
                          <a
                            className="btn btn-danger"
                            onClick={() => settlementPopup(row)}
                            style={{
                              display: "inline-block",
                              fontWeight: "400",
                              textAlign: "center",
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                              userSelect: "none",
                              border: "1px solid transparent",
                              padding: ".375rem .75rem",
                              fontSize: ".875rem",
                              lineHeight: "1.5",
                              borderRadius: ".25rem",
                              color: "white",
                              marginLeft: "5px",
                              transition:
                                "color .15s ease-in-out, background-color .15s ease-in-out, border-color .15s ease-in-out, box-shadow .15s ease-in-out"
                            }}
                          >
                            <strong className="site_family">S</strong>
                          </a>
                        ) : (
                          ""
                        )}

                        {/*
                      <button
                        className="btn btn-info"
                        onClick={() => handleView(row)}
                      >
                        View
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => handleEdit(row)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDelete(row)}
                      >
                        Delete
                      </button>
                  */}
                      </div>
                    )}
                  />
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AgentLayout>
  );
};

export default AccountList;
